/**
 * Where the artwork breaks across a fold, kept current and computed once.
 *
 * Two readers want this — the overlay on every commit, the export sheet once
 * per export — and they must not each grow their own copy. `geometry-tools-audit.md`
 * §2 counts five bugs in one pass with the same cause: a second route to the
 * pixels that nobody told about the rule.
 *
 * ## What is cached, and why each part is
 *
 * | | Rebuilt when | Cost |
 * |---|---|---|
 * | the seam index | body or garment changes | 8.9 ms |
 * | the result | the composite changes | 0.8 ms |
 *
 * The index is the expensive half and the half that almost never changes.
 * Without it a scan walks all 327,015 entries of `across` to find the 6,584
 * that are rim links — 5.59 ms, a third of a frame, on every stroke. With it,
 * 0.198 ms. Both measured on r6-classic/shirt.
 *
 * The result is memoised on a token rather than on the pixels: comparing two
 * 1.3 MB buffers to decide whether to spend 0.2 ms would cost more than the
 * work it saves. The caller bumps the token when it composites, which it is
 * already doing to invalidate the viewer's textures.
 */

import type { Bitmap } from "../../shared/edge";
import {
  checkSeams,
  prepareSeamIndex,
  type SeamCheck,
  type SeamIndex,
} from "../../shared/seam-check";
import type { SeamMap } from "../../shared/seam-map";

export interface SeamBreaksOptions {
  /**
   * Read the composite, or null when it cannot be read.
   *
   * A reader rather than the canvas itself, so this module never touches a 2D
   * context and can be tested in node — the suite has no canvas, and a cache
   * whose invalidation rules are the whole point should not be verifiable only
   * in a browser. It is also called *only* when a recompute is actually due, so
   * the 0.61 ms readback follows the same memoisation as the scan rather than
   * happening on every enquiry.
   */
  read: () => Bitmap | null;
  /** The pairing for the current body and garment, or null before one loads. */
  seam: () => SeamMap | null;
}

export interface SeamBreaks {
  /**
   * The current answer, computed if the composite moved since the last call.
   *
   * Null when there is no body: which faces meet is a fact about a rig, and
   * without one there is nothing to compare across. That is a different state
   * from "no breaks" and the caller has to be able to tell them apart — one
   * means the artwork is fine, the other means we do not know.
   */
  current(): SeamCheck | null;
  /** Mark the composite as changed. Cheap; the work happens on the next read. */
  invalidate(): void;
  /** Drop the index too — the body or the garment changed underneath it. */
  reset(): void;
  /**
   * Build the index now, rather than inside whichever frame reads first.
   *
   * `prepareSeamIndex` is 8.9 ms. Left lazy it lands in the first render after
   * a body or garment change — which is the first frame of the sweep, the one
   * place the cost is visible as a stutter. Called when the map arrives, when
   * nothing is animating.
   */
  warm(): void;
}

export function createSeamBreaks(opts: SeamBreaksOptions): SeamBreaks {
  let index: SeamIndex | null = null;
  let indexFor: SeamMap | null = null;
  let result: SeamCheck | null = null;
  let stale = true;

  return {
    current() {
      const seam = opts.seam();
      if (!seam) {
        result = null;
        return null;
      }
      /*
        Keyed on the map's identity, not on a body id.

        `seamMapFor` caches per body and garment and hands back the same object
        every time, so an identity check is exact and costs nothing. A body id
        would have to be threaded through two more layers to say the same thing
        less reliably.
      */
      if (index === null || indexFor !== seam) {
        index = prepareSeamIndex(seam);
        indexFor = seam;
        stale = true;
      }
      if (!stale && result) return result;

      const bitmap = opts.read();
      if (!bitmap) return result;
      result = checkSeams(bitmap, index, {
        // The overlay draws the broken texels themselves, so it needs them.
        marks: true,
      });
      stale = false;
      return result;
    },
    invalidate() {
      stale = true;
    },
    reset() {
      index = null;
      indexFor = null;
      result = null;
      stale = true;
    },
    warm() {
      const seam = opts.seam();
      if (!seam) return;
      if (index === null || indexFor !== seam) {
        index = prepareSeamIndex(seam);
        indexFor = seam;
        stale = true;
      }
    },
  };
}
